import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { AUTH } from '@/constants/testIds';
import NotificationBell from '@/components/NotificationBell';
import api from '@/lib/api';
import {
    LayoutGrid, Building2, ClipboardCheck,
    Layers, LogOut, Search, Sun, Moon, ChevronRight,
    BookOpen, Camera, MessageSquare, ShieldCheck, MapPinned, ListChecks, AlertTriangle, Radar,
    DollarSign, Package, CreditCard, Activity, Settings, Award,
} from 'lucide-react';

const LOGO_FULL = "https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/bwe3ss1d_1.png";

// Sidebar (alfabetizado por sección, sin duplicados)
const NAV = [
    {
        section: 'Principal',
        items: [
            { to: '/admin', label: 'Panel Principal', icon: LayoutGrid, end: true },
        ],
    },
    {
        section: 'Gestión',
        items: [
            { to: '/admin/catalog', label: 'Catálogo público', icon: BookOpen },
            { to: '/admin/clients', label: 'Clientes', icon: Building2 },
            { to: '/admin/critical-findings', label: 'Hallazgos IA', icon: AlertTriangle },
            { to: '/admin/inspections', label: 'Auditorías', icon: ClipboardCheck },
            { to: '/admin/inspectors', label: 'Auditores', icon: ShieldCheck },
            { to: '/admin/team-map', label: 'Mapa equipo', icon: MapPinned },
            { to: '/admin/contacts', label: 'Mensajes', icon: MessageSquare },
            { to: '/admin/risk-radar', label: 'Radar de Riesgo', icon: Radar },
        ],
    },
    {
        section: 'Normativa',
        items: [
            { to: '/admin/checklist-master', label: 'Checklist Maestro', icon: ListChecks },
            { to: '/admin/settings/certifications', label: 'Biblioteca de Normativas', icon: Award },
            { to: '/admin/settings/citations', label: 'Explicaciones por cita', icon: BookOpen },
        { to: '/admin/ciclos', label: 'Ciclos', icon: Award },
        { to: '/admin/corporativos', label: 'Corporativos', icon: Building2 },
        { to: '/admin/evidencias', label: 'Evidencias', icon: Camera },
            { to: '/admin/rubros', label: 'Rubros', icon: Layers },
        ],
    },
    {
        section: 'Monetización',
        items: [
            { to: '/admin/settings/pricing', label: 'Costos por Rubro', icon: DollarSign },
            { to: '/admin/pricing', label: 'Matriz de Precios (legacy)', icon: DollarSign },
            { to: '/admin/packages', label: 'Paquetes (sin uso)', icon: Package },
            { to: '/admin/payments', label: 'Pagos', icon: CreditCard },
        ],
    },
    {
        section: 'Sistema',
        items: [
            { to: '/admin/diagnostics', label: 'Diagnósticos', icon: Activity },
        ],
    },
    {
        section: 'Ajustes',
        items: [
            { to: '/admin/distintivos', label: 'Distintivos (paquetes)', icon: Award },
        ],
    },
];

const VIOLET = '#7C6BEF';
const VIOLET_DARK = '#5F4FD8';

function timeGreeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
}

function todayLabel() {
    const d = new Date();
    return d.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}


// Recuerda la posicion del menu lateral al cambiar de pantalla, para que
// no te regrese al inicio cada vez que seleccionas una seccion.
function useMenuConMemoria() {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const guardada = sessionStorage.getItem('posicionMenu');
    if (guardada) el.scrollTop = parseInt(guardada, 10) || 0;
    const alDesplazar = () => sessionStorage.setItem('posicionMenu', String(el.scrollTop));
    el.addEventListener('scroll', alDesplazar, { passive: true });
    return () => el.removeEventListener('scroll', alDesplazar);
  }, []);
  return ref;
}

export default function AdminLayout({ children, title = 'Panel Principal' }) {
  const refMenu = useMenuConMemoria();
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (!user) navigate('/login');
        else if (user.role !== 'admin') navigate(`/${user.role}`);
    }, [user, navigate]);

    if (!user) return null;
    const initials = (user.name || 'A').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

    return (
        <div className="h-screen overflow-hidden flex" style={{ background: theme === 'dark' ? '#0B0B14' : '#F5F4FB' }}>
            {/* Sidebar */}
            <aside className="w-[260px] shrink-0 flex flex-col text-white h-screen overflow-y-auto" style={{ background: theme === 'dark' ? '#13121E' : '#1A1830' }}>
                <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-white/10">
                    <img src={LOGO_FULL} alt="AIGIS" className="h-10 w-10 object-contain" />
                    <div>
                        <div className="font-display font-black text-sm tracking-tight leading-none">AIGIS CERTIFICATE</div>
                        <div className="text-[9px] uppercase tracking-[0.25em] text-white/40 mt-1">Admin Platform</div>
                    </div>
                </Link>
                <nav ref={refMenu} className="flex-1 px-3 py-6 overflow-y-auto space-y-6">
                    {NAV.map((section) => (
                        <div key={section.section}>
                            <div className="px-4 text-[10px] uppercase tracking-[0.2em] font-bold text-white/40 mb-2">
                                {section.section}
                            </div>
                            <div className="space-y-1">
                                {section.items.map((item) => {
                                    const active = item.end ? location.pathname === item.to.split('?')[0] : location.pathname.startsWith(item.match || item.to.split('?')[0]);
                                    const Icon = item.icon;
                                    return (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors group relative ${active ? 'text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                                            style={active ? { background: VIOLET } : {}}
                                        >
                                            <Icon className="w-4 h-4 shrink-0" />
                                            <span className="flex-1">{item.label}</span>
                                            {active && <ChevronRight className="w-4 h-4" />}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>
                <div className="px-6 py-4 border-t border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/40">
                    © 2026 AIGIS
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
                {/* Top bar */}
                <header className="border-b sticky top-0 z-30" style={{ background: theme === 'dark' ? '#0B0B14' : '#FFFFFF', borderColor: theme === 'dark' ? '#1F1D2E' : '#E5E7EB' }}>
                    <div className="px-8 py-4 flex items-center justify-between gap-6">
                        <div className="min-w-0">
                            <h1 className="font-display text-xl font-bold truncate" style={{ color: theme === 'dark' ? '#FFFFFF' : '#0A0A0A' }}>{title}</h1>
                            <div className="text-xs capitalize" style={{ color: theme === 'dark' ? '#8B8B9F' : '#6B7280' }}>{todayLabel()}</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="hidden md:flex items-center gap-2 px-3 py-2 min-w-[280px]" style={{ background: theme === 'dark' ? '#13121E' : '#F3F4F6' }}>
                                <Search className="w-4 h-4" style={{ color: theme === 'dark' ? '#666' : '#9CA3AF' }} />
                                <input placeholder="Buscar..." className="flex-1 bg-transparent border-0 outline-none text-sm py-0 px-0" style={{ color: theme === 'dark' ? '#FFFFFF' : '#0A0A0A' }} />
                                <kbd className="text-[10px] px-1.5 py-0.5 border" style={{ borderColor: theme === 'dark' ? '#2A2840' : '#D1D5DB', color: theme === 'dark' ? '#8B8B9F' : '#6B7280' }}>⌘K</kbd>
                            </div>
                            <button onClick={toggle} data-testid="theme-toggle" className="w-10 h-10 inline-flex items-center justify-center transition-colors hover:bg-black/5 dark:hover:bg-white/10" style={{ color: theme === 'dark' ? '#FFC857' : '#7C6BEF' }}>
                                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            </button>
                            <NotificationBell variant={theme === 'dark' ? 'onDark' : 'admin'} />
                            <div className="flex items-center gap-3 pl-3 ml-1 border-l" style={{ borderColor: theme === 'dark' ? '#1F1D2E' : '#E5E7EB' }}>
                                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm text-white" style={{ background: VIOLET }}>
                                    {initials}
                                </div>
                                <div className="hidden sm:block">
                                    <div className="text-sm font-medium" style={{ color: theme === 'dark' ? '#FFFFFF' : '#0A0A0A' }}>{user.name?.split(' ')[0] || 'Admin'}</div>
                                    <div className="flex items-center gap-2 text-[11px]">
                                        <Link to="/profile" data-testid="nav-profile" className="hover:underline inline-flex items-center gap-1" style={{ color: theme === 'dark' ? '#8B8B9F' : '#6B7280' }}>
                                            <ShieldCheck className="w-3 h-3" /> Perfil
                                        </Link>
                                        <span className="opacity-40">·</span>
                                        <button onClick={logout} data-testid={AUTH.logoutButton} className="text-red-500 hover:underline inline-flex items-center gap-1">
                                            <LogOut className="w-3 h-3" /> Salir
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 p-8 overflow-y-auto">{children}</main>

                {/* Discreet version footer (admin panel only) */}
                <AdminVersionFooter theme={theme} />
            </div>
        </div>
    );
}

export { VIOLET, VIOLET_DARK, timeGreeting };

function AdminVersionFooter({ theme }) {
    const [ver, setVer] = React.useState(null);
    React.useEffect(() => {
        let cancelled = false;
        api.get('/version')
            .then((r) => { if (!cancelled) setVer(r.data); })
            .catch(() => { /* silent — footer is best-effort */ });
        return () => { cancelled = true; };
    }, []);
    const version = ver?.version || '—';
    const envLabel = ver?.environment === 'production' ? '' : ' · preview';
    return (
        <footer
            data-testid="admin-version-footer"
            className="px-8 py-2.5 border-t text-[11px] flex items-center justify-end"
            style={{
                background: theme === 'dark' ? '#0B0B14' : '#FFFFFF',
                borderColor: theme === 'dark' ? '#1F1D2E' : '#E5E7EB',
                color: theme === 'dark' ? '#5B5B70' : '#9CA3AF',
            }}
        >
            <span className="font-mono">AIGIS Certificate v{version}{envLabel}</span>
        </footer>
    );
}
